import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Textarea,
  Stack,
} from "@chakra-ui/react";

type Product = {
  attributes: {
    description: {
      standard: string;
    };
    icuLocale: string;
    isFamilyShareable: number;
    kind: string;
    name: string;
    offerName: string;
    offers: {
      currencyCode: string;
      price: string;
      priceFormatted: string;
    }[];
  };
  href: string;
  id: string;
  type: string;
};

type Transaction = {
  originalPurchaseDate: number;
  deviceVerificationNonce: string;
  subscriptionGroupIdentifier: string;
  environment: string;
  inAppOwnershipType: string;
  originalTransactionId: string;
  transactionReason: string;
  storefront: string;
  signedDate: number;
  bundleId: string;
  storefrontId: string;
  transactionId: string;
  type: string;
  webOrderLineItemId: string;
  isUpgraded: boolean;
  productId: string;
  deviceVerification: string;
  purchaseDate: number;
  expiresDate: number;
  quantity: number;
};

const IAPCard: React.FC = () => {
  const [iapLog, setIapLog] = useState("");
  const [iOSIAPCapability, setIOSIAPCapability] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const logMessage = (message: string, consoleOnly = false) => {
    if (consoleOnly) console.log(JSON.parse(message));
    else setIapLog((prev) => prev + `>: ${message}\r\n`);
  };

  const purchaseRequest = (offerName: string) => {
    if (
      iOSIAPCapability &&
      window.webkit?.messageHandlers["iap-purchase-request"]
    ) {
      window.webkit.messageHandlers["iap-purchase-request"].postMessage(
        JSON.stringify({ productID: offerName, quantity: 1 })
      );
    }
  };

  const transactionsRequest = () => {
    if (
      iOSIAPCapability &&
      window.webkit?.messageHandlers["iap-transactions-request"]
    ) {
      window.webkit.messageHandlers["iap-transactions-request"].postMessage(
        "request"
      );
    }
  };

  const productsRequest = () => {
    if (
      iOSIAPCapability &&
      window.webkit?.messageHandlers["iap-products-request"]
    ) {
      window.webkit.messageHandlers["iap-products-request"].postMessage([
        "monthly_sub",
        "weekly_sub",
      ]);
    }
  };

  useEffect(() => {
    if (
      window.webkit?.messageHandlers["iap-purchase-request"] &&
      window.webkit?.messageHandlers["iap-transactions-request"] &&
      window.webkit?.messageHandlers["iap-products-request"]
    ) {
      setIOSIAPCapability(true);
    }

    const handleTransactionsResult = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        logMessage(customEvent.detail, true);
        setTransactions(JSON.parse(customEvent.detail));
      }
    };

    const handlePurchaseResult = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        logMessage(customEvent.detail);
      }
    };

    const handlePurchaseTransaction = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        logMessage(customEvent.detail);
      }
    };

    const handleProductsResult = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        logMessage(customEvent.detail, true);
        setProducts(JSON.parse(customEvent.detail));
      }
    };

    window.addEventListener(
      "iap-transactions-result",
      handleTransactionsResult
    );
    window.addEventListener("iap-purchase-result", handlePurchaseResult);
    window.addEventListener(
      "iap-purchase-transaction",
      handlePurchaseTransaction
    );
    window.addEventListener("iap-products-result", handleProductsResult);

    return () => {
      window.removeEventListener(
        "iap-transactions-result",
        handleTransactionsResult
      );
      window.removeEventListener("iap-purchase-result", handlePurchaseResult);
      window.removeEventListener(
        "iap-purchase-transaction",
        handlePurchaseTransaction
      );
      window.removeEventListener("iap-products-result", handleProductsResult);
    };
  }, [iOSIAPCapability]);

  return (
    <Card className="p-4">
      <CardHeader className="flex justify-between items-center">
        <h2>In-App Purchase</h2>
        <p>Store Kit 2</p>
      </CardHeader>
      <CardBody>
        <Tabs>
          <TabList>
            <Tab>Products</Tab>
            <Tab>Transactions</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Offer</Th>
                    <Th>Name</Th>
                    <Th>Kind</Th>
                    <Th isNumeric>Price</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {products.map((product) => (
                    <Tr key={product.id}>
                      <Td>{product.attributes.offerName}</Td>
                      <Td>{product.attributes.name}</Td>
                      <Td>{product.attributes.kind}</Td>
                      <Td isNumeric>
                        {product.attributes.offers[0]?.priceFormatted}
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          onClick={() =>
                            purchaseRequest(product.attributes.offerName)
                          }
                        >
                          Buy
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>
            <TabPanel>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>id</Th>
                    <Th>product</Th>
                    <Th>state</Th>
                    <Th>type</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {transactions.map((transaction) => (
                    <Tr key={transaction.transactionId}>
                      <Td>{transaction.originalTransactionId}</Td>
                      <Td>{transaction.productId}</Td>
                      <Td>{transaction.inAppOwnershipType}</Td>
                      <Td>{transaction.type}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </CardBody>
      <CardFooter>
        <Stack width="100%">
          <Textarea
            size="sm"
            isReadOnly
            value={iapLog}
            placeholder="events log"
          />
          <Button
            colorScheme="blue"
            onClick={() => {
              productsRequest();
              transactionsRequest();
            }}
          >
            Request Data
          </Button>
        </Stack>
      </CardFooter>
    </Card>
  );
};

export default IAPCard;
